/**
 * Lettura di uno stream `text/event-stream` ottenuto con `fetch`.
 *
 * `EventSource` non sa fare POST ne' mandare l'intestazione `Authorization`,
 * quindi lo stream si legge dal corpo della risposta. I pezzi arrivano come il
 * trasporto decide — a meta' riga, a meta' di un carattere multibyte, piu'
 * eventi insieme — e qui si ricompongono. Un evento con `data` che non e' JSON
 * valido arriva con `dati: null`: non interrompe lo stream, ma chi consuma lo
 * vede e decide.
 */

export interface EventoSse {
  evento: string;
  dati: unknown;
}

export async function* leggiEventiSse(risposta: Response): AsyncGenerator<EventoSse> {
  if (!risposta.body) return;
  const lettore = risposta.body.getReader();
  // `stream: true` tiene in sospeso i byte di un carattere spezzato fra due pezzi.
  const decodifica = new TextDecoder("utf-8");
  let residuo = "";

  const emetti = function* (blocco: string): Generator<EventoSse> {
    let evento = "message";
    const dati: string[] = [];
    for (const riga of blocco.split("\n")) {
      if (riga.startsWith("event:")) evento = riga.slice(6).trim();
      else if (riga.startsWith("data:")) dati.push(riga.slice(5).trim());
      // Commenti (`:`), `id:`, `retry:` e righe ignote non interessano.
    }
    if (dati.length === 0) return;
    try {
      yield { evento, dati: JSON.parse(dati.join("\n")) };
    } catch {
      // JSON malformato: l'evento arriva lo stesso, senza dati, cosi' chi
      // consuma sa che c'era (un `fine` o un `errore` illeggibile non devono
      // sparire e far passare lo stream per concluso bene).
      yield { evento, dati: null };
    }
  };

  try {
    for (;;) {
      const { value, done } = await lettore.read();
      if (done) break;
      // I ritorni carrello si tolgono subito: cosi' il separatore e' sempre
      // "\n\n", anche quando il server manda CRLF e il taglio cade in mezzo.
      residuo += decodifica.decode(value, { stream: true }).replace(/\r/g, "");
      let separatore: number;
      while ((separatore = residuo.indexOf("\n\n")) !== -1) {
        const blocco = residuo.slice(0, separatore);
        residuo = residuo.slice(separatore + 2);
        yield* emetti(blocco);
      }
    }
    residuo += decodifica.decode().replace(/\r/g, "");
    if (residuo.trim()) yield* emetti(residuo);
  } finally {
    lettore.releaseLock();
  }
}
